import { collapseResponsibleSelfBack } from "./responsableBack.js";

export function getContextualBackTarget(defaultTarget = "/responsable") {
  if (typeof window === "undefined") return defaultTarget;
  const params = new URLSearchParams(window.location.search || "");
  const rawBack = String(params.get("back") || "").trim();
  const isSupervisionPath = (pathname) => /^\/(multisecteur\/)?superviseur(\/|$)/.test(String(pathname || ""));
  const isResponsiblePath = (pathname) => /^\/(multisecteur\/)?responsable(\/|$)/.test(String(pathname || ""));
  const isPortalMedecinPath = (pathname) => /^\/(multisecteur\/)?portail-medecin\/?$/.test(String(pathname || ""));
  if (rawBack) {
    try {
      let url = new URL(rawBack, window.location.origin);
      if (url.origin === window.location.origin && isResponsiblePath(url.pathname)) {
        url = new URL(collapseResponsibleSelfBack(`${url.pathname}${url.search || ""}${url.hash || ""}`), window.location.origin);
      }
      if (url.origin === window.location.origin && isSupervisionPath(url.pathname)) {
        return `${url.pathname}${url.search || ""}${url.hash || ""}`;
      }
      if (url.origin === window.location.origin && isResponsiblePath(url.pathname)) {
        const nestedBack = url.searchParams.get("back") || "";
        if (nestedBack) {
          const nestedUrl = new URL(nestedBack, window.location.origin);
          if (nestedUrl.origin === window.location.origin && isSupervisionPath(nestedUrl.pathname)) {
            try {
              const lockedHubTarget = getLockedResponsibleSectorHubTarget(url);
              if (lockedHubTarget) return lockedHubTarget;
            } catch (_) {
              // Hors contexte de pont verrouille, on conserve le comportement historique.
            }
            return `${nestedUrl.pathname}${nestedUrl.search || ""}${nestedUrl.hash || ""}`;
          }
          if (nestedUrl.origin === window.location.origin && isPortalMedecinPath(nestedUrl.pathname)) {
            const lockedHubTarget = getLockedResponsibleSectorHubTarget(url);
            if (lockedHubTarget) return lockedHubTarget;
            return `${nestedUrl.pathname}${nestedUrl.search || ""}${nestedUrl.hash || ""}`;
          }
        }
        return `${url.pathname}${url.search || ""}${url.hash || ""}`;
      }
      if (url.origin === window.location.origin && isPortalMedecinPath(url.pathname)) {
        return `${url.pathname}${url.search || ""}${url.hash || ""}`;
      }
    } catch (_) {
      // Conserver le fallback existant si le paramètre back n'est pas exploitable.
    }
  }
  return defaultTarget;
}

function getSupervisionBridgeSectorFromCurrentUrl(currentUrl) {
  if (!currentUrl || !/^\/responsable(\/|$)/.test(currentUrl.pathname)) return "";
  if (currentUrl.searchParams.get("lockSecteur") !== "1") return "";

  if (currentUrl.pathname === "/responsable/thoracique" || currentUrl.pathname === "/responsable/planning-thoracique") {
    return "sect-thoracique";
  }

  if (currentUrl.pathname === "/responsable/oph-liste-medecins") {
    return "sect-oph";
  }

  const typeToSecteur = {
    ASTREINTE_THORACIQUE: "sect-thoracique",
    DIGESTIF: "sect-digestif",
    INTERNE_DIGESTIF: "sect-digestif",
    REANIMATION: "sect-reanimation",
    INTERNE_REA: "sect-reanimation",
    ORTHOPEDIE: "sect-orthopedie",
    ORTHOPEDIE_JUNIOR: "sect-orthopedie",
    DOULEUR: "sect-douleur",
    UCA: "sect-uca",
    OPH: "sect-oph",
    PR1: "sect-port-royal",
    PR2: "sect-port-royal",
    INTERNE1: "sect-port-royal",
    INTERNE2: "sect-port-royal",
  };
  const type = String(currentUrl.searchParams.get("type") || "").trim().toUpperCase();
  if (
    /^\/responsable\/gardes($|-)/.test(currentUrl.pathname) ||
    currentUrl.pathname === "/responsable/gardes-liste-medecins"
  ) {
    return typeToSecteur[type] || "";
  }

  return "";
}

function isTransversalSupervisorBridgeScreen(currentUrl) {
  if (!currentUrl || currentUrl.searchParams.get("lockSecteur") !== "1") return false;
  return (
    currentUrl.pathname === "/responsable/equipe" ||
    currentUrl.pathname === "/responsable/vie-service"
  );
}

function getLockedResponsibleSectorHubTarget(url) {
  if (typeof window === "undefined") return "";
  try {
    const currentUrl = new URL(window.location.href);
    const currentSecteur = getSupervisionBridgeSectorFromCurrentUrl(currentUrl);
    const targetSecteur = url.searchParams.get("secteur") || "";
    const targetIsResponsibleSectorHub =
      /^\/(multisecteur\/)?responsable\/index\.html$/.test(url.pathname) &&
      targetSecteur &&
      targetSecteur === currentSecteur;

    if (url.origin === window.location.origin && targetIsResponsibleSectorHub) {
      return `${url.pathname}${url.search || ""}${url.hash || ""}`;
    }
    return "";
  } catch (_) {
    return "";
  }
}

function hasSupervisorBackChain(rawBack, depth = 0) {
  if (!rawBack || depth > 5 || typeof window === "undefined") return false;
  try {
    const url = new URL(rawBack, window.location.origin);
    if (url.origin !== window.location.origin) return false;
    if (/^\/(multisecteur\/)?superviseur(\/|$)/.test(url.pathname)) return true;
    const nestedBack = url.searchParams.get("back") || "";
    return nestedBack ? hasSupervisorBackChain(nestedBack, depth + 1) : false;
  } catch (_) {
    return false;
  }
}

function getSupervisionBridgeRestoreContext(target) {
  if (typeof window === "undefined") return false;
  try {
    const targetUrl = new URL(target, window.location.origin);
    const currentUrl = new URL(window.location.href);
    const targetIsSupervisor = /^\/(multisecteur\/)?superviseur\/index\.html$/.test(targetUrl.pathname);

    if (isTransversalSupervisorBridgeScreen(currentUrl) && targetIsSupervisor) {
      return "transversal";
    }

    const currentSecteur = getSupervisionBridgeSectorFromCurrentUrl(currentUrl);
    if (!currentSecteur || !Boolean(currentUrl.searchParams.get("back"))) return "";

    const targetIsResponsibleSectorHub =
      /^\/(multisecteur\/)?responsable\/index\.html$/.test(targetUrl.pathname) &&
      targetUrl.searchParams.get("secteur") === currentSecteur &&
      hasSupervisorBackChain(targetUrl.searchParams.get("back") || "");

    if (
      targetUrl.origin === window.location.origin &&
      (targetIsSupervisor || targetIsResponsibleSectorHub)
    ) {
      return currentSecteur;
    }
    return "";
  } catch (_) {
    return "";
  }
}

async function restoreSupervisionBridgeSessionIfNeeded(target, { keepalive = false } = {}) {
  const context = getSupervisionBridgeRestoreContext(target);
  if (!context) return;
  try {
    const currentUrl = new URL(window.location.href);
    const response = await fetch("/api/v2/auth/supervision-bridge/restore", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      keepalive,
      body: JSON.stringify({
        context,
        current: `${currentUrl.pathname}${currentUrl.search || ""}${currentUrl.hash || ""}`,
      }),
    });
    if (response.ok) {
      sessionStorage.removeItem("auth_prefer_session_v1");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
    }
  } catch (_) {
    // Si la restauration échoue, on laisse la navigation existante trancher.
  }
}

function navigateToRestoredTarget(url, restoreContext) {
  const targetHref = `${url.pathname}${url.search || ""}${url.hash || ""}`;
  const canReturnToOpener =
    Boolean(restoreContext) &&
    typeof window !== "undefined" &&
    window.opener &&
    !window.opener.closed;
  const keepsPortRoyalPanelOpen =
    /^\/(multisecteur\/)?responsable\/index\.html$/.test(url.pathname) &&
    url.searchParams.get("secteur") === "sect-port-royal" &&
    url.searchParams.get("open") === "liste-medecins";

  // La fermeture doit être tentée dans le geste utilisateur, avant l'aller-
  // retour asynchrone vers l'API : sinon certains navigateurs la refusent.
  if (canReturnToOpener && !keepsPortRoyalPanelOpen) {
    try {
      const currentUrl = new URL(window.location.href);
      window.opener.postMessage({
        type: "supervision-bridge-return",
        context: restoreContext,
        current: `${currentUrl.pathname}${currentUrl.search || ""}${currentUrl.hash || ""}`,
      }, window.location.origin);
      if (typeof window.opener.focus === "function") window.opener.focus();
    } catch (_) {
      // Repli contextuel ci-dessous si l'opener est inaccessible.
    }
    // Le parent qui a créé le popup peut le fermer de manière fiable. Si ce
    // mécanisme est indisponible, on conserve la navigation contextuelle.
    window.setTimeout(() => {
      if (window.closed) return;
      restoreSupervisionBridgeSessionIfNeeded(targetHref, { keepalive: true }).finally(() => {
        window.location.href = targetHref;
      });
    }, 1200);
    return;
  }

  const finishNavigation = () => {
    window.location.href = targetHref;
  };

  if (!restoreContext) {
    finishNavigation();
    return;
  }

  restoreSupervisionBridgeSessionIfNeeded(targetHref).finally(finishNavigation);
}

export function returnToContextualTarget(defaultTarget = "/responsable") {
  const target = getContextualBackTarget(defaultTarget);
  const url = new URL(target, window.location.origin);
  navigateToRestoredTarget(url, getSupervisionBridgeRestoreContext(`${url.pathname}${url.search || ""}${url.hash || ""}`));
}

export function goContextualBack(navigate, defaultTarget = "/responsable") {
  const target = getContextualBackTarget(defaultTarget);
  const url = new URL(target, window.location.origin);
  const restoreContext = getSupervisionBridgeRestoreContext(`${url.pathname}${url.search || ""}${url.hash || ""}`);
  const mustReloadStaticResponsiblePage =
    url.origin === window.location.origin &&
    (
      /^\/responsable\/?$/.test(url.pathname) ||
      /^\/responsable\/index\.html$/.test(url.pathname) ||
      /^\/multisecteur\/responsable\/index\.html$/.test(url.pathname)
    ) &&
    url.searchParams.has("secteur");
  if (/^\/(multisecteur\/)?(superviseur|portail-medecin)(\/|$)/.test(target) || mustReloadStaticResponsiblePage) {
    if (mustReloadStaticResponsiblePage && url.pathname === "/responsable") {
      url.pathname = "/responsable/";
      window.location.href = `${url.pathname}${url.search || ""}${url.hash || ""}`;
      return;
    }
    navigateToRestoredTarget(url, restoreContext);
    return;
  }
  navigate(target);
}
